import { message } from 'antd';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import _get from 'lodash/get';
import _isObject from 'lodash/isObject';

import {
  GET_AUDIENCE_INFO,
  GET_AUDIENCE_INFO_FAILED,
  GET_AUDIENCE_INFO_SUCCESS
} from 'containers/Reward/constants';
import { cleanOutcomeOptions } from 'containers/Rule/form-v2/utils';
import { sanitizeFilters } from 'utils/audience';
import { TGlobalListPage } from 'utils/globalTranslation/TModules';

import { DURATION_TIMER } from '../../appConstants';
import {
  CREATE_REWARD,
  CREATE_REWARD_SUCCESS,
  CREATE_REWARD_FAIL,
  APPROVE_REWARD,
  APPROVE_REWARD_SUCCESS,
  APPROVE_REWARD_FAIL,
  DEACTIVATE_REWARD,
  DEACTIVATE_REWARD_SUCCESS,
  DEACTIVATE_REWARD_FAIL,
  REACTIVATE_REWARD,
  REACTIVATE_REWARD_SUCCESS,
  REACTIVATE_REWARD_FAIL,
  UPDATE_REWARD,
  UPDATE_REWARD_SUCCESS,
  UPDATE_REWARD_FAIL,
  GET_REWARDS,
  GET_REWARDS_SUCCESS,
  GET_REWARDS_FAIL,
  GET_REWARD,
  GET_REWARD_SUCCESS,
  GET_REWARD_FAIL,
  GET_TAGS,
  GET_TAGS_SUCCESS,
  GET_TAGS_FAIL,
  GET_AUDIENCES,
  GET_AUDIENCES_SUCCESS,
  GET_AUDIENCES_FAIL,
  GET_BRANDS,
  GET_BRANDS_SUCCESS,
  GET_BRANDS_FAIL,
  GET_MERCHANTS,
  GET_MERCHANTS_SUCCESS,
  GET_MERCHANTS_FAIL,
  GET_LOYALTY_LIST,
  GET_LOYALTY_LIST_SUCCESS,
  GET_LOYALTY_LIST_FAIL,
  SEARCH_REWARD,
  SEARCH_REWARD_SUCCESS,
  SEARCH_REWARD_FAIL,
  UPLOAD_AUDIENCE,
  UPLOAD_AUDIENCE_SUCCESS,
  SELECT_SCHEDULE,
  DUPLICATE_REWARD,
  DUPLICATE_REWARD_SUCCESS,
  DUPLICATE_REWARD_FAIL,
  GET_PARTNER,
  GET_PARTNER_FAIL,
  GET_PARTNER_SUCCESS
} from './constants';
import { TStatusText } from './i18n/TModules';

const cleanRewardPrices = (prices: any[]) => {
  if (!Array.isArray(prices) || prices.length === 0) return prices;

  return prices.map((price: any) => {
    if (price?.outcome_options && _isObject(price.outcome_options)) {
      return {
        ...price,
        outcome_options: cleanOutcomeOptions(price.outcome_options)
      };
    }
    return price;
  });
};

export const searchReward = payload => {
  return {
    type: SEARCH_REWARD,
    endpoint: `/v4/dash/rewards`,
    types: [SEARCH_REWARD, SEARCH_REWARD_SUCCESS, SEARCH_REWARD_FAIL],
    params: payload,
    notification: {
      error: () => {
        message.error(t(`${TStatusText}.CouldNotRetrieveRewards`), DURATION_TIMER);
      }
    }
  };
};

export const partnerGet = (payload, callback) => {
  return {
    type: GET_PARTNER,
    endpoint: `/v4/dash/partners/${payload}`,
    types: [GET_PARTNER, GET_PARTNER_FAIL, GET_PARTNER_SUCCESS],
    params: payload,
    notification: {
      error: () => {
        message.error(t(`${TStatusText}.ErrorObtainingPartnerDetails`), DURATION_TIMER);
      }
    },
    callback
  };
};

export const rewardCreate = (payload, callback, history) => {
  let data = payload;
  for (let k in data) {
    if (k.indexOf('rt_terms_and_conditions') >= 0) {
      delete data[k];
    }
  }
  if (!data['include_audience_file']) {
    delete data['include_audience_file'];
  }
  if (!data['exclude_audience_file']) {
    delete data['exclude_audience_file'];
  }
  if (data.filters) {
    data.filters = sanitizeFilters(data.filters);
  }
  if (data && data.code_generation_method === 'multiple') {
    if (!data.user_generated) {
      data.code_generation_method = 'multiple_with_user_upload';
    }
  }

  if (data?.reward_prices) {
    data.reward_prices = cleanRewardPrices(data.reward_prices);
  }
  const isMarketplaceRewardCreate = Boolean(data?.marketplace_reward_type);

  // change the keys name of the redemption_data array field
  if ('redemption_data' in data) {
    data.redemption_data.query_params_values = data.redemption_data.query_params_values.map(
      ({ id, key, mapping, ...rest }) => ({ key_id: id, ...rest })
    );
    data.redemption_data.body_params_values = data.redemption_data.body_params_values
      .map((item: any) => {
        if (item?.prefix === 'None') item.prefix = null;
        if (item?.postfix === 'None') item.postfix = null;
        return item;
      })
      .map(({ id, key, mapping, ...rest }) => ({ key_id: id, ...rest }));
  }
  return {
    type: CREATE_REWARD,
    endpoint: `/v4/dash/rewards`,
    method: 'post',
    types: [CREATE_REWARD, CREATE_REWARD_SUCCESS, CREATE_REWARD_FAIL],
    callback: callback,
    notification: {
      loading: () => {
        message.loading(t(`${TStatusText}.CreatingReward`));
      },
      success: response => {
        if (response?.meta?.reward_request_id) {
          message.success(t(`${TStatusText}.RewardSubmittedForApproval`), DURATION_TIMER);
        } else if (isMarketplaceRewardCreate && response?.data?.id) {
          message.success(`Reward id: ${response.data.id} Created successfully.`, DURATION_TIMER);
        } else {
          message.success(t(`${TStatusText}.success`), DURATION_TIMER);
        }
      },
      error: error => {
        if (error?.message) {
          message.error(error.message, DURATION_TIMER);
        } else {
          message.error(t(`${TStatusText}.FailedToCreateReward`), DURATION_TIMER);
        }
      }
    },
    instantAction: response => history(`/p/rewards/show/${response.data.id}`),
    formdata: data
  };
};

export const rewardApprove = (payload, callBack?) => {
  return {
    type: APPROVE_REWARD,
    endpoint: `/v4/dash/rewards/${payload.id}/approve`,
    method: 'patch',
    types: [APPROVE_REWARD, APPROVE_REWARD_SUCCESS, APPROVE_REWARD_FAIL],
    notification: {
      loading: () => {
        message.loading(t(`${TStatusText}.RewardApproval`));
      },
      success: () => {
        message.success(t(`${TStatusText}.ApprovalSuccess`), DURATION_TIMER);
      },
      error: err => {
        const msg = _get(err, 'message', t(`${TStatusText}.FailedToApprove`));
        message.error(msg, DURATION_TIMER);
      }
    },
    callback: callBack,
    formdata: payload
  };
};

export const rewardDeactivate = (payload, callBack?) => {
  return {
    type: DEACTIVATE_REWARD,
    endpoint: `/v4/dash/rewards/${payload.id}/deactivate`,
    method: 'patch',
    types: [DEACTIVATE_REWARD, DEACTIVATE_REWARD_SUCCESS, DEACTIVATE_REWARD_FAIL],
    notification: {
      loading: () => {
        message.loading(t(`${TStatusText}.RewardDeactivation`));
      },
      success: response => {
        if (response?.data?.request_type) {
          message.success(
            t(`${TStatusText}.DeactivationRequestSubmittedForApproval`),
            DURATION_TIMER
          );
        } else {
          message.success(t(`${TStatusText}.DeactivationSuccess`), DURATION_TIMER);
        }
      },
      error: () => {
        message.error(t(`${TStatusText}.FailedToDeactivate`), DURATION_TIMER);
      }
    },
    callback: callBack,
    formdata: payload
  };
};

export const rewardReactivate = (payload, callBack?) => {
  return {
    type: REACTIVATE_REWARD,
    endpoint: `/v4/dash/rewards/${payload.id}/activate`,
    method: 'patch',
    types: [REACTIVATE_REWARD, REACTIVATE_REWARD_SUCCESS, REACTIVATE_REWARD_FAIL],
    notification: {
      loading: () => {
        message.loading(t(`${TStatusText}.RewardReactivation`));
      },
      success: response => {
        if (!response?.data?.request_type) {
          message.success(t(`${TStatusText}.ReactivationSuccess`), DURATION_TIMER);
          return;
        }
        message.success(
          t(`${TStatusText}.ReactivationRequestSubmittedForApproval`),
          DURATION_TIMER
        );
      },
      error: () => {
        message.error(t(`${TStatusText}.FailedToReactivate`), DURATION_TIMER);
      }
    },
    callback: callBack,
    formdata: payload
  };
};

export const rewardUpdate = (payload, callback?, history?) => {
  let data = payload.toUpdate;
  let diffData = payload.diffData || null;

  if (data.reward_prices) {
    data.reward_prices = cleanRewardPrices(data.reward_prices);
  }

  if (diffData?.reward_prices) {
    diffData.reward_prices = cleanRewardPrices(diffData.reward_prices);
  }

  const reward_request_id = payload.reward_request_id || null;
  const makerCheckerReward = payload.makerCheckerReward || null;
  const isRoleTenantAdmin = payload.isRoleTenantAdmin || false;

  for (let k in data) {
    if (k.indexOf('rt_terms_and_conditions') >= 0) {
      delete data[k];
    }
  }
  if (!data['include_audience_file']) {
    delete data['include_audience_file'];
  }
  if (!data['exclude_audience_file']) {
    delete data['exclude_audience_file'];
  }
  if (data.filters) {
    data.filters = sanitizeFilters(data.filters);
  }

  if (data && data.code_generation_method === 'multiple') {
    if (!data.user_generated) {
      data.code_generation_method = 'multiple_with_user_upload';
    }
  }

  if (data && data.merchant_account_id && _isObject(data.merchant_account_id)) {
    data.merchant_account_id = data.merchant_account_id.id;
  }

  //Need to explicitly send null value to overwrite existing ends_at value in database
  if (data && !data?.ends_at) {
    data.ends_at = null;
  }

  // change the key name of the redemption_data array field
  if ('redemption_data' in data) {
    data.redemption_data.query_params_values = data.redemption_data.query_params_values.map(
      ({ id, key, mapping, ...rest }) => ({ key_id: id, ...rest })
    );
    data.redemption_data.body_params_values = data.redemption_data.body_params_values
      .map((item: any) => {
        if (item?.prefix === 'None') item.prefix = null;
        if (item?.postfix === 'None') item.postfix = null;
        return item;
      })
      .map(({ id, key, mapping, ...rest }) => ({ key_id: id, ...rest }));
  }

  let requestConfig = {} as {
    method: string;
    endpoint: string;
    formData: Record<string, any>;
    shouldCheckForMeta: boolean;
  };

  switch (true) {
    /* Update new changes for existing reward request */
    case makerCheckerReward && !isEmpty(reward_request_id) && isRoleTenantAdmin === false:
      requestConfig = {
        method: 'put',
        endpoint: `v4/dash/reward_requests/${reward_request_id}`,
        formData: {
          properties: diffData
        },
        shouldCheckForMeta: false
      };
      break;
    /* Apply changes to the reward for editing active reward */
    case makerCheckerReward &&
      !reward_request_id &&
      payload.toUpdate.state !== 'draft' &&
      isRoleTenantAdmin === false:
      requestConfig = {
        method: 'patch',
        endpoint: `/v4/dash/rewards/${payload.toUpdate.id}/apply_changes`,
        formData: {
          properties: diffData
        },
        shouldCheckForMeta: false
      };
      break;
    default:
      requestConfig = {
        method: 'put',
        endpoint: `/v4/dash/rewards/${data.id}`,
        formData: data,
        shouldCheckForMeta: true
      };
  }

  return {
    type: UPDATE_REWARD,
    endpoint: requestConfig.endpoint,
    method: requestConfig.method,
    types: [UPDATE_REWARD, UPDATE_REWARD_SUCCESS, UPDATE_REWARD_FAIL],
    callback: callback,
    notification: {
      loading: () => {
        message.loading(t(`${TStatusText}.Processing`));
      },
      success: response => {
        if (requestConfig.shouldCheckForMeta) {
          if (response?.meta?.reward_request_id) {
            message.success(t(`${TStatusText}.RewardSubmittedForApproval`), DURATION_TIMER);
          } else {
            message.success(t(`${TStatusText}.success`), DURATION_TIMER);
          }
        } else {
          message.success(t(`${TStatusText}.RewardSubmittedForApproval`), DURATION_TIMER);
        }
      },
      error: e => {
        if (e?.message) {
          message.error(e.message, DURATION_TIMER);
          console.error(e);
        } else {
          message.error(t(`${TStatusText}.FailedToUpdateReward`), DURATION_TIMER);
        }
      }
    },
    // nextAction: payload.page && payload.page === 'list' ? rewardsGet({ state: 'active' }) : {},
    instantAction: response => {
      if (data.page) {
        if (data.page === 'list') {
          // yield call(rewardsGet, {})
        } else {
          history.push(`/p/rewards/show/${data.id}`);
        }
      } else {
        if (response?.data?.request_type) {
          const recordId = response?.data?.reward_campaign?.id;
          const rewardRequestId = response?.data?.id;
          const creatorId = response?.data?.creator_id;

          if (recordId && rewardRequestId && creatorId) {
            const state = {
              rewardRequestId: rewardRequestId,
              rewardCreatorId: creatorId
            };
            history.push({
              pathname: `/p/rewards/show/${recordId}/reward_request/${rewardRequestId}`,
              state
            });
          } else {
            history.push(`/p/rewards/show/${data.id}`);
          }
        } else {
          history.push(`/p/rewards/show/${data.id}`);
        }
      }
    },
    formdata: requestConfig.formData
  };
};

export const rewardUpdateSuccess = payload => {
  return {
    type: UPDATE_REWARD_SUCCESS,
    payload
  };
};

export const rewardsGet = payload => {
  return {
    type: GET_REWARDS,
    endpoint: `/v4/dash/rewards`,
    types: [GET_REWARDS, GET_REWARDS_SUCCESS, GET_REWARDS_FAIL],
    params: payload,
    notification: {
      error: error => {
        if (error.message) {
          message.error(error.message, DURATION_TIMER);
        } else {
          message.error(t(`${TStatusText}.ErrorLoadingRewards`), DURATION_TIMER);
        }
      }
    }
  };
};

export const rewardGet = (payload, callBack?) => {
  const id = payload?.id;
  const reward_request_id = payload?.reward_request_id;

  return {
    type: GET_REWARD,
    method: 'get',
    endpoint: reward_request_id
      ? `/v4/dash/reward_requests/${reward_request_id}/reward`
      : `/v4/dash/rewards/${id}`,
    types: [GET_REWARD, GET_REWARD_SUCCESS, GET_REWARD_FAIL],
    notification: {
      error: error => {
        if (error.message) {
          message.error(error.message, DURATION_TIMER);
        } else {
          message.error(t(`${TStatusText}.ErrorLoadingRewards`), DURATION_TIMER);
        }
      }
    },
    params: payload?.params || {},
    callback: callBack
  };
};

//TAGS for rewards fetch here
export const tagsGet = (payload?) => {
  return {
    type: GET_TAGS,
    endpoint: `/v4/dash/simple/tags?size=1000`,
    types: [GET_TAGS, GET_TAGS_SUCCESS, GET_TAGS_FAIL],
    notification: {
      error: () => {
        message.error(t(`${TStatusText}.ErrorLoadingTags`), DURATION_TIMER);
      }
    },
    payload
  };
};

//TAGS for rewards fetch here
export const brandsGet = (payload?) => {
  return {
    type: GET_BRANDS,
    endpoint: '/v4/dash/simple/brands',
    types: [GET_BRANDS, GET_BRANDS_SUCCESS, GET_BRANDS_FAIL],
    notification: {
      error: () => {
        message.error(t(`${TStatusText}.ErrorLoadingBrands`), DURATION_TIMER);
      }
    },
    payload
  };
};

//TAGS for audiences fetch here

export const audienceListGet = (payload?) => {
  const params = payload || {};
  const endpoint = params.fullAudience ? '/v4/dash/audiences' : '/v4/dash/simple/audiences';
  return {
    type: GET_AUDIENCES,
    endpoint: endpoint,
    types: [GET_AUDIENCES, GET_AUDIENCES_SUCCESS, GET_AUDIENCES_FAIL],
    notification: {
      error: () => {
        message.error(t(`${TStatusText}.ErrorLoadingAudiences`), DURATION_TIMER);
      }
    },
    payload
  };
};

export const audienceFileUpload = (payload, processDone) => {
  const toastKey = 'audience-upload-submitting';
  const getErrorMessage = error => {
    if (!error) return null;
    if (typeof error === 'string') return error;
    if (typeof error.message === 'string') return error.message;
    if (typeof error.error === 'string') return error.error;
    if (Array.isArray(error.errors) && error.errors.length > 0) return error.errors.join(', ');
    if (typeof error.errors === 'string') return error.errors;
    if (typeof error?.data?.message === 'string') return error.data.message;
    if (typeof error?.data?.error === 'string') return error.data.error;
    if (Array.isArray(error?.data?.errors) && error.data.errors.length > 0) {
      return error.data.errors.join(', ');
    }
    return null;
  };
  return {
    type: UPLOAD_AUDIENCE,
    endpoint: `/v4/dash/audiences`,
    method: 'post',
    types: [UPLOAD_AUDIENCE, UPLOAD_AUDIENCE_SUCCESS, ''],
    notification: {
      loading: () => {
        message.loading({
          content: t(`${TStatusText}.SubmittingData`),
          duration: 0,
          key: toastKey
        });
        return () => {};
      },
      success: () => {
        message.success({
          content: t(`${TStatusText}.FileUploadSuccess`),
          duration: DURATION_TIMER,
          key: toastKey
        });
      },
      error: e => {
        const errorMessage = getErrorMessage(e);
        if (errorMessage) {
          message.error({ content: errorMessage, duration: DURATION_TIMER, key: toastKey });
        } else {
          message.error({
            content: t(`${TGlobalListPage}.SomethingWentWrong`),
            duration: DURATION_TIMER,
            key: toastKey
          });
        }
      }
    },
    callback: processDone,
    formdata: payload
  };

  // return {
  //   type: UPLOAD_AUDIENCE,
  //   payload,
  //   processDone,
  // }
};

// export const audienceFileUploadSuccess = payload => ({
//   type: UPLOAD_AUDIENCE_SUCCESS,
//   payload,
// })

//TAGS for rewards fetch here
export const merchantsGet = (payload?) => {
  return {
    type: GET_MERCHANTS,
    endpoint: `/v4/dash/simple/merchants`,
    types: [GET_MERCHANTS, GET_MERCHANTS_SUCCESS, GET_MERCHANTS_FAIL],
    notification: {
      error: () => {
        message.error(t(`${TStatusText}.ErrorLoadingMerchants`), DURATION_TIMER);
      }
    },
    payload
  };
};

//TAGS for loyalty fetch here
export const loyaltylistGet = (payload?) => {
  return {
    type: GET_LOYALTY_LIST,
    endpoint: `/v4/dash/simple/loyalty`,
    types: [GET_LOYALTY_LIST, GET_LOYALTY_LIST_SUCCESS, GET_LOYALTY_LIST_FAIL],
    notification: {
      error: () => {
        message.error(t(`${TStatusText}.ErrorLoadingLoyaltyList`), DURATION_TIMER);
      }
    },
    payload
  };
};

export const selectSchedule = payload => {
  return {
    type: SELECT_SCHEDULE,
    payload
  };
};

export const rewardDuplicate = (payload, callback) => {
  return {
    type: DUPLICATE_REWARD,
    endpoint: `/v4/dash/rewards/${payload.id}/duplicate`,
    method: 'post',
    types: [DUPLICATE_REWARD, DUPLICATE_REWARD_SUCCESS, DUPLICATE_REWARD_FAIL],
    notification: {
      loading: () => {
        message.loading(t(`${TStatusText}.DuplicatingReward`));
      },
      success: () => {
        message.success(t(`${TStatusText}.success`) + '...', DURATION_TIMER);
      },
      error: () => {
        message.error(t(`${TStatusText}.FailedToDuplicate`), DURATION_TIMER);
      }
    },
    instantAction: () => {
      rewardsGet({});
    },
    formdata: payload,
    callback: callback
  };
};

export const audienceInfoGet = payload => {
  return {
    type: GET_AUDIENCE_INFO,
    endpoint: `/v4/dash/simple/audiences`,
    types: [GET_AUDIENCE_INFO, GET_AUDIENCE_INFO_SUCCESS, GET_AUDIENCE_INFO_FAILED],
    notification: {
      error: () => {
        message.error(t(`${TStatusText}.FailedToLoadData`), DURATION_TIMER);
      }
    },
    params: payload
  };
};
